import { EMAIL_QUERY_CONFIG } from '../pages/dashboard/emails/constants.ts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EmailService } from '../lib/emailService.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { supabase } from '../lib/supabaseClient.ts';
import { toast } from 'sonner';

// Query Keys
export const emailKeys = {
  all: ['emails'] as const,
  pending: (userId: string) => [...emailKeys.all, 'pending', userId] as const,
  sent: (userId: string) => [...emailKeys.all, 'sent', userId] as const,
  stats: (userId: string) => [...emailKeys.all, 'stats', userId] as const,
  job: (jobId: string) => [...emailKeys.all, 'job', jobId] as const,
  prompts: (userId: string) => [...emailKeys.all, 'prompts', userId] as const,
  searchJobs: (userId: string, searchTerm: string, statusFilter?: string) => [...emailKeys.all, 'searchJobs', userId, searchTerm, statusFilter] as const,
  searchSent: (userId: string, searchTerm: string) =>
    [...emailKeys.all, 'searchSent', userId, searchTerm] as const,
  businessName: (userId: string) => [...emailKeys.all, 'businessName', userId] as const,
  unified: (userId: string, page: number, pageSize: number, searchTerm: string, statusFilter: string) => 
    [...emailKeys.all, 'unified', userId, page, pageSize, searchTerm, statusFilter] as const,
};

export function useEmails(
  page: number = 1, 
  pageSize: number = 10, 
  searchTerm: string = '', 
  statusFilter: string = 'all'
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: emailKeys.unified(user?.id || '', page, pageSize, searchTerm, statusFilter),
    queryFn: () => EmailService.getAllEmailsWithSearch(user?.id || '', page, pageSize, searchTerm, statusFilter),
    enabled: !!user?.id,
    refetchInterval: EMAIL_QUERY_CONFIG.SENT_REFRESH_INTERVAL,
    staleTime: EMAIL_QUERY_CONFIG.STALE_TIME_MEDIUM,
  });
}

export function useEmailStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: emailKeys.stats(user?.id || ''),
    queryFn: () => EmailService.getEmailStats(user?.id || ''),
    enabled: !!user?.id,
    refetchInterval: EMAIL_QUERY_CONFIG.STATS_REFRESH_INTERVAL,
    staleTime: EMAIL_QUERY_CONFIG.STALE_TIME_SHORT,
  });
}

export function useRefreshEmailData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: emailKeys.all });
    },
    onSuccess: () => {
      toast.success('Email data refreshed');
    },
    onError: () => toast.error('Failed to refresh email data'),
  });
}


export function useBusinessName() {
  const { user } = useAuth();

  return useQuery({
    queryKey: emailKeys.businessName(user?.id || ''),
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('user_settings')
        .select('business_name')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching business name:', error);
        return null;
      }

      return data?.business_name || null;
    },
    enabled: !!user?.id,
    staleTime: EMAIL_QUERY_CONFIG.STALE_TIME_EXTRA_LONG,
  });
}

export function useUpdateEmailAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, newEmail }: { jobId: string; newEmail: string }) => 
      EmailService.updateEmailAddress(jobId, newEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.all });
      toast.success('Email address updated successfully');
    },
    onError: (error) => {
      console.error('Error updating email address:', error);
      toast.error('Failed to update email address');
    },
  });
}


export function useResendEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId }: { jobId: string }) => EmailService.resendEmail(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.all });
      toast.success('Email queued for resending');
    },
    onError: (error) => {
      console.error('Error resending email:', error);
      toast.error('Failed to resend email');
    },
  });
}

export function useDeleteEmailJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => EmailService.deleteEmailJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.all });
      toast.success('Email record deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting email job:', error);
      toast.error('Failed to delete email record');
    },
  });
}

export function usePreCallEmailsEnabled() {
  const { user } = useAuth();

  return useQuery({
      queryKey: ['email-settings', user?.id],
      queryFn: async () => {
          if (!user?.id) return null;
          const { data } = await supabase
              .from('email_settings')
              .select('settings')
              .eq('user_id', user.id)
              .single();
          return data?.settings?.pre_meeting?.enabled || false;
      },
      enabled: !!user?.id
  });
}

export async function sendPreCallEmails(
  leads: any[], 
  instruction: string, 
  businessName: string, 
  userId: string, 
  isEnabled: boolean,
  callId: string,
) {
  if (!isEnabled) return;
  const leadsWithEmail = leads.filter(lead => lead.customer_email);
  if (leadsWithEmail.length === 0) return;

  for (const lead of leadsWithEmail) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-precall-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          to: lead.customer_email,
          clientFullName: lead.customer_name || "Valued Customer",
          instruction: instruction,
          businessName: businessName,
          callId: callId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Email API error for ${lead.customer_email}:`, response.status, errorText);
        throw new Error(`Email API returned ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error(`Failed to send pre-call email to ${lead.customer_email}:`, error);
    }
  }
}

export function useSendPreCallEmails() {

  return useMutation({
    mutationFn: async ({ leads, instruction, businessName, userId, isEnabled, callId }: {
      leads: any[];
      instruction: string;
      businessName: string;
      userId: string;
      isEnabled: boolean;
      callId: string;
    }) => {
      return sendPreCallEmails(leads, instruction, businessName, userId, isEnabled, callId);
    },
  });
}